import {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { isEqual } from 'lodash';
import axios, { Canceler } from 'axios';
import {
  IUseTableFetchProps,
  IUseTableFetchReturn,
  TUseTableFetchRefetchMethod,
  TUseTableFetchRequest,
} from './interfaces';
import useCachedValue from '../useCachedValue';

type TSetIntervalReturn = ReturnType<typeof setInterval>;

interface IResponse<Entity> {
  data: IUseTableFetchReturn<Entity>['data'];
  total: IUseTableFetchReturn<Entity>['total'];
}

const DEFAULT_RESPONSE = { data: [], total: 0 };

const useTableFetch = <Params, Request extends TUseTableFetchRequest<Params>, Entity>(
  props: IUseTableFetchProps<Params, Request>,
) : IUseTableFetchReturn<Entity> => {
  const {
    request,
    refetchIntervalRequest = request,
    params = {},
    skip = false,
    autoRefetch = false,
    refetchInterval = 15000,
    clearIntervalOnError = true,
    onRequestError,
  } = props;

  const [isLoading, setIsLoading] = useState<IUseTableFetchReturn<Entity>['isLoading']>(false);
  const [isFetchedAfterMount, setIsFetchedAfterMount] = useState<
    IUseTableFetchReturn<Entity>['isFetchedAfterMount']
  >(false);
  const [response, setResponse] = useState<IResponse<Entity>>(DEFAULT_RESPONSE);
  const intervalID = useRef<TSetIntervalReturn | null>(null);
  const cachedResponse = useRef<IResponse<Entity>>();
  const cachedParams = useCachedValue<Params>(params as Params);
  const requestCanceler = useRef<Canceler | null>(null);
  const intervalRequestCanceler = useRef<Canceler | null>(null);

  const handleInterval = useCallback(
    async () => {
      try {
        const newResponse = await refetchIntervalRequest({
          ...cachedParams as Params,
          cancelToken: new axios.CancelToken((canceler) => {
            intervalRequestCanceler.current = canceler;
          }),
        });
        if (!isEqual(cachedResponse.current, newResponse)) {
          setResponse(newResponse as IResponse<Entity>);
          cachedResponse.current = newResponse as IResponse<Entity>;
        }
        intervalRequestCanceler.current = null;
      } catch (error: unknown) {
        if (clearIntervalOnError) {
          clearInterval(intervalID.current as TSetIntervalReturn);
          intervalID.current = null;
        }
        if (axios.isCancel(error)) {
          // requests, which were canceled by the hook, should not be handled as errored requests
          return;
        }
        if (onRequestError) {
          onRequestError(error);
        }
        setResponse(DEFAULT_RESPONSE);
      }
    },
    [refetchIntervalRequest, cachedParams, clearIntervalOnError, onRequestError],
  );

  const fetch = useCallback<TUseTableFetchRefetchMethod>(
    async (isSilentUpdate = false) => {
      try {
        clearInterval(intervalID.current as TSetIntervalReturn);
        intervalID.current = null;
        if (!isSilentUpdate) {
          setIsLoading(true);
        }
        const newResponse = await request({
          ...cachedParams as Params,
          cancelToken: new axios.CancelToken((canceler) => {
            requestCanceler.current = canceler;
          }),
        });
        setResponse(newResponse as IResponse<Entity>);
        cachedResponse.current = newResponse as IResponse<Entity>;
        requestCanceler.current = null;
        if (autoRefetch) {
          intervalID.current = setInterval(handleInterval, refetchInterval);
        }
        if (!isSilentUpdate) {
          setIsLoading(false);
        }
        setIsFetchedAfterMount(true);
      } catch (error: unknown) {
        if (clearIntervalOnError) {
          clearInterval(intervalID.current as TSetIntervalReturn);
          intervalID.current = null;
        }
        if (axios.isCancel(error)) {
          // requests, which were canceled by the hook, should not be handled as errored requests
          return;
        }
        if (onRequestError) {
          onRequestError(error);
        }
        setResponse(DEFAULT_RESPONSE);
        if (!isSilentUpdate) {
          setIsLoading(false);
        }
        setIsFetchedAfterMount(true);
      }
    },
    [
      request,
      cachedParams,
      autoRefetch,
      handleInterval,
      refetchInterval,
      clearIntervalOnError,
      onRequestError,
    ],
  );

  useLayoutEffect(() => {
    if (skip) {
      return undefined;
    }
    fetch();
    return () => {
      /*
        cancel any in progress requests on unmount
        will handle props change and unmount
      */
      if (requestCanceler.current) {
        /*
          for the cases when the previous request hasn't been fulfilled yet, but props have
          changed already; will cancel the previous request
        */
        requestCanceler.current();
      }
      if (intervalRequestCanceler.current) {
        // for the cases when the interval's request is in progress, but props have changed already
        intervalRequestCanceler.current();
      }
    };
  }, [fetch, skip]);

  useEffect(() => () => {
    clearInterval(intervalID.current as TSetIntervalReturn);
    return undefined;
  }, []);

  const { data, total } = response;
  return {
    isLoading,
    isFetchedAfterMount,
    data,
    total,
    refetch: fetch,
  };
};

export * from './interfaces';
export default useTableFetch;
